/**
 * כרטיס לקוח — פרטים · היסטוריית הצעות · מחירים מיוחדים · מחיקה (TASKS 5.4, PRD 7.3).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminNav } from './AdminNav.js';
import { AdminPageHeader } from './AdminPageHeader.js';
import { FormCard } from './FormCard.js';
import { isAdminApiError } from './adminApi.js';
import { StatusMessage } from './StatusMessage.js';
import {
  deleteCustomer,
  deleteCustomerPrice,
  getCustomer,
  updateCustomer,
  upsertCustomerPrice,
  type Customer,
  type CustomerPrice,
  type QuoteSummary,
} from './customersApi.js';
import { getProduct, listProducts, type ProductListItem, type Variant } from './productsApi.js';

const PRICE_BASIS_LABEL: Record<QuoteSummary['price_basis'], string> = {
  base: 'מחיר בסיס',
  customer: 'מחיר מיוחד',
  manual: 'ידני',
};

export function CustomerDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const customerId = Number(id);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [prices, setPrices] = useState<CustomerPrice[]>([]);
  const [quotes, setQuotes] = useState<QuoteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [notes, setNotes] = useState('');
  const [isRegular, setIsRegular] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerFormError, setCustomerFormError] = useState<string | null>(null);

  const [priceSearch, setPriceSearch] = useState('');
  const [priceSearchResults, setPriceSearchResults] = useState<ProductListItem[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductListItem | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<Variant[]>([]);
  const [selectedVariantId, setSelectedVariantId] = useState<number | ''>('');
  const [fixedPrice, setFixedPrice] = useState('');
  const [priceFormError, setPriceFormError] = useState<string | null>(null);
  const [addingPrice, setAddingPrice] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteResult, setDeleteResult] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function refetch() {
    const result = await getCustomer(customerId);
    setCustomer(result.customer);
    setPrices(result.prices);
    setQuotes(result.quotes);
    setFullName(result.customer.full_name);
    setPhone(result.customer.phone_e164);
    setEmail(result.customer.email ?? '');
    setCompany(result.customer.company ?? '');
    setNotes(result.customer.notes ?? '');
    setIsRegular(Boolean(result.customer.is_regular));
  }

  useEffect(() => {
    if (!Number.isInteger(customerId) || customerId <= 0) return;
    let cancelled = false;
    setLoading(true);
    refetch()
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת הלקוח.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  async function handleSaveCustomer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCustomerFormError(null);
    setSavingCustomer(true);
    try {
      await updateCustomer(customerId, {
        fullName: fullName.trim(),
        phone: phone.trim(),
        email: email.trim() || null,
        company: company.trim() || null,
        notes: notes.trim() || null,
        isRegular,
      });
      await refetch();
    } catch (err) {
      setCustomerFormError(isAdminApiError(err) ? err.message : 'שגיאה בשמירת פרטי הלקוח.');
    } finally {
      setSavingCustomer(false);
    }
  }

  async function handleProductSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPriceFormError(null);
    try {
      const result = await listProducts({ q: priceSearch.trim() || undefined });
      setPriceSearchResults(result.products);
    } catch (err) {
      setPriceFormError(isAdminApiError(err) ? err.message : 'שגיאה בחיפוש מוצרים.');
    }
  }

  async function handleSelectProduct(product: ProductListItem) {
    setSelectedProduct(product);
    setSelectedVariantId('');
    setPriceSearchResults([]);
    try {
      const { variants } = await getProduct(product.id);
      setSelectedVariants(variants.filter((v) => v.is_active));
    } catch {
      setSelectedVariants([]);
    }
  }

  async function handleAddPrice(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedProduct) {
      setPriceFormError('יש לבחור מוצר קודם.');
      return;
    }
    setPriceFormError(null);
    setAddingPrice(true);
    try {
      await upsertCustomerPrice(customerId, {
        productId: selectedProduct.id,
        variantId: selectedVariantId === '' ? null : selectedVariantId,
        fixedPrice: Number(fixedPrice),
      });
      setSelectedProduct(null);
      setSelectedVariants([]);
      setSelectedVariantId('');
      setFixedPrice('');
      setPriceSearch('');
      await refetch();
    } catch (err) {
      setPriceFormError(isAdminApiError(err) ? err.message : 'שגיאה בהוספת מחיר מיוחד.');
    } finally {
      setAddingPrice(false);
    }
  }

  async function handleDeletePrice(priceId: number) {
    setPriceFormError(null);
    try {
      await deleteCustomerPrice(customerId, priceId);
      await refetch();
    } catch (err) {
      setPriceFormError(isAdminApiError(err) ? err.message : 'שגיאה במחיקת מחיר מיוחד.');
    }
  }

  async function handleDeleteCustomer() {
    setDeleteError(null);
    try {
      const { mode } = await deleteCustomer(customerId);
      setDeleteResult(
        mode === 'deleted'
          ? 'הלקוח נמחק לגמרי - לא היו לו הצעות.'
          : 'ללקוח יש היסטוריית הצעות, ולכן פרטיו האישיים הוחלפו בפלייסהולדר. ההצעות עצמן נשארו לתיעוד עסקי.',
      );
      setTimeout(() => navigate('/admin/customers', { replace: true }), 2500);
    } catch (err) {
      setDeleteError(isAdminApiError(err) ? err.message : 'שגיאה במחיקת הלקוח.');
    }
  }

  if (loading) {
    return (
      <>
        <AdminNav />
        <p className="text-muted p-8">טוען…</p>
      </>
    );
  }

  if (loadError || !customer) {
    return (
      <>
        <AdminNav />
        <p role="alert" className="text-danger p-8">
          {loadError ?? 'הלקוח לא נמצא.'}
        </p>
      </>
    );
  }

  return (
    <>
      <AdminNav />
      <main className="mx-auto max-w-3xl px-6 py-12">
        <AdminPageHeader title={`כרטיס לקוח - ${customer.full_name}`} />

        <form className="mt-6" onSubmit={(e) => void handleSaveCustomer(e)}>
          <FormCard title="פרטי לקוח">
            <div>
              <label htmlFor="c-name" className="block text-sm font-semibold">
                שם מלא
              </label>
              <input
                id="c-name"
                required
                aria-required="true"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div>
              <label htmlFor="c-phone" className="block text-sm font-semibold">
                טלפון
              </label>
              <input
                id="c-phone"
                required
                aria-required="true"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="ltr-inline mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div>
              <label htmlFor="c-email" className="block text-sm font-semibold">
                דוא&quot;ל
              </label>
              <input
                id="c-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="ltr-inline mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div>
              <label htmlFor="c-company" className="block text-sm font-semibold">
                חברה
              </label>
              <input
                id="c-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div>
              <label htmlFor="c-notes" className="block text-sm font-semibold">
                הערות
              </label>
              <textarea
                id="c-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isRegular}
                onChange={(e) => setIsRegular(e.target.checked)}
              />
              לקוח קבוע
            </label>
          </FormCard>

          {/* ⭐ DESIGN.md 8.4 — כפתור שמירה דביק. */}
          <div className="bg-canvas border-line sticky bottom-0 -mx-6 mt-6 flex items-center gap-4 border-t px-6 py-4">
            {customerFormError && (
              <p role="alert" className="text-danger text-sm">
                {customerFormError}
              </p>
            )}
            <button type="submit" className="btn btn-cta" disabled={savingCustomer}>
              {savingCustomer ? 'שומר…' : 'שמירה'}
            </button>
          </div>
        </form>

        <section
          className="border-line bg-surface mt-8 rounded-lg border p-6"
          aria-labelledby="prices-h"
        >
          <h2 id="prices-h" className="text-lg font-semibold">
            מחירים מיוחדים
          </h2>
          <p className="text-muted mt-1 text-sm">
            סכום קבוע בש&quot;ח, לא אחוז. שינוי מחיר הבסיס של המוצר לא משנה מחיר שכבר נשמר כאן.
          </p>

          {prices.length === 0 && (
            <p className="text-muted mt-3 text-sm">אין עדיין מחירים מיוחדים.</p>
          )}

          {prices.length > 0 && (
            <ul className="mt-3 space-y-2">
              {prices.map((price) => (
                <li
                  key={price.id}
                  className="border-line flex items-center justify-between rounded border p-2 text-sm"
                >
                  <span>
                    <span className="ltr-inline font-semibold">{price.product_sku}</span> -{' '}
                    {price.product_name}
                    {price.variant_label && <> · {price.variant_label}</>}
                    {' - '}
                    <span className="tabular">₪{price.fixed_price}</span>
                    <span className="text-muted">
                      {' '}
                      (מחיר בסיס נוכחי: ₪{price.variant_price ?? price.product_base_price})
                    </span>
                  </span>
                  <button
                    type="button"
                    className="rounded border border-line px-2 py-1"
                    onClick={() => void handleDeletePrice(price.id)}
                  >
                    מחיקה
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-5 border-t border-line pt-4">
            <form
              className="grid gap-3 sm:grid-cols-[2fr_auto]"
              onSubmit={(e) => void handleProductSearch(e)}
            >
              <div>
                <label htmlFor="price-product-q" className="block text-sm font-semibold">
                  חיפוש מוצר (שם או מק&quot;ט)
                </label>
                <input
                  id="price-product-q"
                  value={priceSearch}
                  onChange={(e) => setPriceSearch(e.target.value)}
                  className="mt-1 w-full rounded-md border border-line px-3 py-2"
                />
              </div>
              <div className="flex items-end">
                <button type="submit" className="btn btn-secondary">
                  חיפוש
                </button>
              </div>
            </form>

            {priceSearchResults.length > 0 && (
              <ul className="mt-3 space-y-1">
                {priceSearchResults.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      className="w-full rounded border border-line px-2 py-1 text-start"
                      onClick={() => void handleSelectProduct(product)}
                    >
                      <span className="ltr-inline">{product.sku}</span> - {product.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {selectedProduct && (
              <form
                className="mt-4 grid gap-3 sm:grid-cols-[2fr_1fr_1fr_auto] items-end"
                onSubmit={(e) => void handleAddPrice(e)}
              >
                <p className="text-sm sm:col-span-4">
                  נבחר: <span className="ltr-inline">{selectedProduct.sku}</span> -{' '}
                  {selectedProduct.name}
                </p>

                {selectedVariants.length > 0 && (
                  <div>
                    <label htmlFor="price-variant" className="block text-sm font-semibold">
                      ואריאנט (אופציונלי)
                    </label>
                    <select
                      id="price-variant"
                      value={selectedVariantId}
                      onChange={(e) =>
                        setSelectedVariantId(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      className="mt-1 w-full rounded-md border border-line px-3 py-2"
                    >
                      <option value="">- בלי ואריאנט -</option>
                      {selectedVariants.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label htmlFor="price-fixed" className="block text-sm font-semibold">
                    מחיר מיוחד (₪)
                  </label>
                  <input
                    id="price-fixed"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    aria-required="true"
                    value={fixedPrice}
                    onChange={(e) => setFixedPrice(e.target.value)}
                    className="tabular ltr-inline mt-1 w-full rounded-md border border-line px-3 py-2"
                  />
                </div>

                <button type="submit" className="btn btn-cta" disabled={addingPrice}>
                  {addingPrice ? 'שומר…' : 'שמירת מחיר'}
                </button>
              </form>
            )}
          </div>

          {priceFormError && (
            <p role="alert" className="text-danger mt-3 text-sm">
              {priceFormError}
            </p>
          )}
        </section>

        <section
          className="border-line bg-surface mt-8 rounded-lg border p-6"
          aria-labelledby="quotes-h"
        >
          <h2 id="quotes-h" className="text-lg font-semibold">
            היסטוריית הצעות
          </h2>

          {quotes.length === 0 && (
            <p className="text-muted mt-3 text-sm">אין עדיין הצעות ללקוח זה.</p>
          )}

          {quotes.length > 0 && (
            <table className="mt-3 w-full border-collapse text-sm">
              <caption className="sr-only">היסטוריית הצעות ללקוח {customer.full_name}</caption>
              <thead>
                <tr className="border-line border-b text-start">
                  <th scope="col" className="p-2 text-start">
                    מספר הצעה
                  </th>
                  <th scope="col" className="p-2 text-start">
                    סכום כולל מע&quot;מ
                  </th>
                  <th scope="col" className="p-2 text-start">
                    מקור מחיר
                  </th>
                  <th scope="col" className="p-2 text-start">
                    תאריך
                  </th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((quote) => (
                  <tr key={quote.id} className="border-line border-b">
                    <td className="p-2">
                      <span className="ltr-inline">{quote.quote_number}</span>
                    </td>
                    <td className="tabular p-2">₪{quote.total}</td>
                    <td className="p-2">{PRICE_BASIS_LABEL[quote.price_basis]}</td>
                    <td className="p-2">
                      {new Date(quote.created_at).toLocaleDateString('he-IL')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section
          className="border-line bg-surface mt-8 rounded-lg border p-6"
          aria-labelledby="delete-h"
        >
          <h2 id="delete-h" className="text-lg font-semibold">
            מחיקת פרטי לקוח לפי בקשה
          </h2>
          <p className="text-muted mt-1 text-sm">
            לקוח בלי היסטוריית הצעות יימחק לגמרי. לקוח עם הצעות - פרטיו האישיים יוחלפו בפלייסהולדר
            וההצעות עצמן יישארו כתיעוד עסקי.
          </p>

          {deleteResult && (
            <StatusMessage className="mt-3 text-sm font-semibold">{deleteResult}</StatusMessage>
          )}
          {deleteError && (
            <p role="alert" className="text-danger mt-3 text-sm">
              {deleteError}
            </p>
          )}

          {!deleteResult && (
            <div className="mt-3">
              {confirmDelete ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="text-danger rounded border border-line px-3 py-1"
                    onClick={() => void handleDeleteCustomer()}
                  >
                    לאשר מחיקה
                  </button>
                  <button
                    type="button"
                    className="rounded border border-line px-3 py-1"
                    onClick={() => setConfirmDelete(false)}
                  >
                    ביטול
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="rounded border border-line px-3 py-1"
                  onClick={() => setConfirmDelete(true)}
                >
                  מחיקת פרטי הלקוח
                </button>
              )}
            </div>
          )}
        </section>
      </main>
    </>
  );
}
